import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, makeStyles, useMediaQuery } from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

const useStyles = makeStyles((theme) => ({
  paper: {
    height: "90vh",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
  },
  shell: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  nav: {
    width: 210,
    flexShrink: 0,
    borderRight: `1px solid ${theme.mode === "dark" ? "#334155" : "#eef0f5"}`,
    padding: theme.spacing(2, 1.5),
    overflowY: "auto",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8fafc",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      display: "flex",
      gap: 4,
      padding: theme.spacing(0.5, 1),
      boxSizing: "border-box",
      overflowX: "auto",
      overflowY: "hidden",
      borderRight: "none",
      borderBottom: `1px solid ${theme.mode === "dark" ? "#334155" : "#eef0f5"}`,
    },
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    border: "none",
    background: "transparent",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 4,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: 14,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      flexShrink: 0,
      minHeight: 44,
      padding: "8px 12px",
      marginBottom: 0,
      whiteSpace: "nowrap",
      gap: 6,
    },
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "#eef0ff",
    },
  },
  navItemActive: {
    backgroundColor: theme.mode === "dark" ? "rgba(79,70,245,0.18)" : "#eef0ff",
    color: theme.palette.primary.main,
    fontWeight: 800,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  navDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#ef4444",
    marginLeft: "auto",
  },
  config: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2.5, 2.5, 3),
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    [theme.breakpoints.down("sm")]: {
      minHeight: 0,
      padding: theme.spacing(2),
      overflowX: "hidden",
    },
  },
  preview: {
    width: 300,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.mode === "dark" ? "#334155" : "#eef0f5"}`,
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
    backgroundColor: theme.mode === "dark" ? "#0f172a" : "#fbfcfe",
  },
  mobileTools: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
    flexShrink: 0,
    padding: theme.spacing(0.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    "& .MuiButton-root": { minHeight: 44, minWidth: 0, fontSize: 12 },
  },
  previewButton: {
    minHeight: 44,
    whiteSpace: "nowrap",
  },
  previewDialogContent: {
    overflowY: "auto",
    minHeight: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.mode === "dark" ? "#0f172a" : "#fbfcfe",
  },
  previewActions: {
    flexShrink: 0,
    padding: "8px 16px calc(8px + env(safe-area-inset-bottom, 0px))",
    "& .MuiButton-root": { minHeight: 44 },
  },
}));

export const campaignWizardPaperStyle = {
  height: "90vh",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 16,
};

export const campaignWizardPaper = compact => compact ? {
  ...campaignWizardPaperStyle,
  height: "100dvh",
  maxHeight: "100dvh",
  borderRadius: 0,
} : campaignWizardPaperStyle;

export const CampaignPreview = ({ children }) => {
  const classes = useStyles();
  const compact = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  if (!compact) return <Box className={classes.preview}>{children}</Box>;
  return <>
    <Button type="button" className={classes.previewButton} color="primary" startIcon={<VisibilityOutlinedIcon />}
      onClick={() => setOpen(true)}>Vista previa</Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullScreen aria-labelledby="campaign-mobile-preview-title">
      <DialogTitle id="campaign-mobile-preview-title">Vista previa de la campaña</DialogTitle>
      <DialogContent className={classes.previewDialogContent}>{children}</DialogContent>
      <DialogActions className={classes.previewActions}>
        <Button type="button" variant="contained" color="primary" fullWidth onClick={() => setOpen(false)}>Volver a la campaña</Button>
      </DialogActions>
    </Dialog>
  </>;
};

const CampaignWizardLayout = ({
  sections = [],
  activeSection,
  onSectionChange,
  preview,
  children,
}) => {
  const classes = useStyles();
  const compact = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const configRef = useRef(null);
  const navRef = useRef(null);
  const sectionIndex = sections.findIndex(section => section.id === activeSection);
  useEffect(() => {
    if (configRef.current) configRef.current.scrollTop = 0;
    if (compact) navRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [activeSection, compact]);

  return (
    <Box className={classes.shell}>
      <Box className={classes.nav} role="tablist" aria-label="Opciones de la campaña" ref={navRef}>
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            role="tab"
            aria-selected={activeSection === section.id}
            tabIndex={activeSection === section.id ? 0 : -1}
            aria-label={`${section.label}${section.error ? ": revisa los campos" : ""}`}
            className={`${classes.navItem} ${activeSection === section.id ? classes.navItemActive : ""}`}
            onClick={() => onSectionChange(section.id)}
            onKeyDown={event => {
              const index = sections.findIndex(item => item.id === section.id);
              const next = event.key === "ArrowRight" ? (index + 1) % sections.length
                : event.key === "ArrowLeft" ? (index - 1 + sections.length) % sections.length
                  : event.key === "Home" ? 0 : event.key === "End" ? sections.length - 1 : -1;
              if (next < 0) return;
              event.preventDefault();
              onSectionChange(sections[next].id);
              navRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
            }}
          >
            {section.icon}
            <Typography variant="body2" style={{ fontWeight: "inherit", color: "inherit" }}>
              {section.label}
            </Typography>
            {section.error ? <span className={classes.navDot} /> : null}
          </button>
        ))}
      </Box>
      <Box className={classes.config} ref={configRef} role="region" aria-label="Configuración de la campaña">{children}</Box>
      {compact ? <Box className={classes.mobileTools}>
        <Button type="button" disabled={sectionIndex <= 0} onClick={() => onSectionChange(sections[sectionIndex - 1].id)}>Anterior</Button>
        {preview && <CampaignPreview>{preview}</CampaignPreview>}
        <Button type="button" color="primary" disabled={sectionIndex < 0 || sectionIndex >= sections.length - 1}
          onClick={() => onSectionChange(sections[sectionIndex + 1].id)}>Siguiente</Button>
      </Box> : preview && <CampaignPreview>{preview}</CampaignPreview>}
    </Box>
  );
};

export default CampaignWizardLayout;
